import React from "react";
import { Table, TableBody, TableCell, TableRow } from '@material-ui/core';
import moment from 'moment'
import { CLASE_TABLA_DETALLE } from '../../general/Detalle';

// Piezas comunes de las pestañas del detalle: una fila gris de título por sección y debajo
// nombre | valor. Las filas sin valor no se muestran, y una sección sin filas tampoco.
const ESTILO_SECCION = { fontWeight: 600, background: '#f5f5f5' };
const ESTILO_VACIO = { color: '#8a8a8a' };
const vacio = (valor) => valor === null || valor === undefined || valor === '';
const unirNombre = (...partes) => partes.filter(Boolean).join(' ');
const formatoFecha = (fecha) => moment(fecha).format('YYYY-MM-DD, h:mm a');

function TablaDetalle({ children }) {
    return (
        <Table className={CLASE_TABLA_DETALLE}>
            <TableBody>{children}</TableBody>
        </Table>
    )
}

// filas: [[nombre, valor], ...]
function SeccionDetalle({ titulo, filas }) {
    const visibles = filas.filter(([, valor]) => !vacio(valor));
    if (!visibles.length) return null;
    return (
        <React.Fragment>
            <TableRow>
                <TableCell colSpan={2} style={ESTILO_SECCION}>{titulo}</TableCell>
            </TableRow>
            {visibles.map(([nombre, valor], i) => (
                <TableRow key={i}>
                    <TableCell component="th" scope="row">{nombre}</TableCell>
                    <TableCell align="left">{valor}</TableCell>
                </TableRow>
            ))}
        </React.Fragment>
    )
}

export function InscripcionDetalleDatos({ inscripcion }) {
    let { estudiante, celular1, telefono1, fecha_registro, estado_actual, grupo, discapacidad, gestor, codigo_pruebas, puntaje_pruebas, puntaje_sisben, sisben, nombre_acudiente, celular_acudiente, correo_acudiente, tipo_identificacion_saber, identificacion_saber, canal_contacto } = inscripcion;
    // El formulario v2 ya no pide el documento de Saber 11 y lo envía copiado del aspirante: solo se
    // muestra cuando es distinto (premédico y las inscripciones anteriores sí lo pedían aparte)
    const saberDistinto = String(identificacion_saber || '') !== String(estudiante.identificacion || '')
        || (tipo_identificacion_saber?.id ?? null) !== (estudiante.tipo_identificacion?.id ?? null);
    return (
        <TablaDetalle>
            <SeccionDetalle titulo="Identificación" filas={[
                ['Nombre completo', unirNombre(estudiante.primer_nombre, estudiante.segundo_nombre, estudiante.primer_apellido, estudiante.segundo_apellido)],
                ['Tipo de identificación', estudiante.tipo_identificacion?.nombre],
                ['Identificación', estudiante.identificacion],
            ]} />
            <SeccionDetalle titulo="Datos de contacto" filas={[
                ['Correo', estudiante.correo],
                ['Teléfono fijo', telefono1],
                ['Celular', celular1],
                ['Canal de contacto', canal_contacto?.nombre],
            ]} />
            <SeccionDetalle titulo="Acudiente" filas={[
                ['Nombre', nombre_acudiente],
                ['Celular', celular_acudiente],
                ['Correo', correo_acudiente],
            ]} />
            <SeccionDetalle titulo="Pruebas Saber 11" filas={[
                ['Código de registro', codigo_pruebas],
                ['Puntaje', puntaje_pruebas],
                ['Tipo de identificación Saber', saberDistinto ? tipo_identificacion_saber?.nombre : null],
                ['Identificación Saber', saberDistinto ? identificacion_saber : null],
            ]} />
            <SeccionDetalle titulo="Comunidad y Sisben" filas={[
                ['Comunidad especial', grupo?.nombre],
                ['Discapacidad', discapacidad?.nombre],
                ['Sisben', sisben ? sisben.toUpperCase() : null],
                ['Puntaje Sisben', sisben == 'si' ? puntaje_sisben : null],
            ]} />
            <SeccionDetalle titulo="Gestión" filas={[
                ['Estado actual', estado_actual?.nombre],
                ['Gestor', gestor ? unirNombre(gestor.primer_nombre, gestor.primer_apellido, gestor.segundo_apellido) : null],
                ['Correo del gestor', gestor?.correo],
                ['Fecha de registro', formatoFecha(fecha_registro)],
            ]} />
        </TablaDetalle>
    )
}

export function InscripcionDetalleContacto({ inscripcion }) {
    let { departamento, ciudad, estrato, barrio, direccion, nombre_eps, nombre_otro_eps } = inscripcion;
    return (
        <TablaDetalle>
            <SeccionDetalle titulo="Ubicación" filas={[
                ['Departamento', departamento?.nombre],
                ['Ciudad o municipio', ciudad?.nombre],
                ['Barrio', barrio],
                ['Dirección', direccion],
                ['Estrato', estrato?.nombre],
            ]} />
            <SeccionDetalle titulo="Salud" filas={[
                ['EPS', nombre_eps?.nombre ?? 'Ninguna'],
                ['Otra EPS', nombre_otro_eps],
            ]} />
        </TablaDetalle>
    )
}

export function InscripcionDetalleCarrera({ inscripcion }) {
    let { generacion, carrera, conocio } = inscripcion;
    if (generacion === 'otro') generacion = 'ESPERANDO RESULTADO'
    return (
        <TablaDetalle>
            <SeccionDetalle titulo="Programa" filas={[
                ['Carrera', carrera?.nombre],
                ['Cómo nos conoció', conocio ? conocio.nombre : 'EDU.co'],
                ['Generación E', generacion ? generacion.toUpperCase() : null],
            ]} />
        </TablaDetalle>
    )
}

// Caracterización del formulario v2 (pasos "Bienestar y salud" y "Vida universitaria") y, al final,
// las autorizaciones del paso Confirmación
export function InscripcionDetalleCaracterizacion({ caracterizacion }) {
    if (caracterizacion.error) return <p style={{ padding: 24, textAlign: 'center' }}>No se pudo cargar la caracterización. Intenta de nuevo.</p>;
    if (!caracterizacion.existe) return <p style={{ padding: 24, textAlign: 'center' }}>Esta inscripción no tiene caracterización.</p>;
    const { secciones, aceptaciones, fecha_aceptacion } = caracterizacion;
    const autorizacion = (valor) => valor === true ? 'Sí' : valor === false ? 'No' : 'No aplica';
    // Opciones múltiples una por línea; sin respuesta: "No aplica" (condicional) o "Sin respuesta" (opcional)
    const respuesta = ({ respuestas, vacia }) => respuestas.length
        ? respuestas.map((r, i) => <div key={i} style={{ whiteSpace: 'pre-line' }}>{r}</div>)
        : <span style={ESTILO_VACIO}>{vacia}</span>;
    return (
        <TablaDetalle>
            {secciones.map(({ codigo, titulo, preguntas }) => (
                <SeccionDetalle key={codigo} titulo={titulo} filas={preguntas.map((p) => [p.pregunta, respuesta(p)])} />
            ))}
            <SeccionDetalle titulo="Autorizaciones" filas={[
                ['Políticas de protección de datos', autorizacion(aceptaciones.politica)],
                ['Consentimiento informado (Psicología)', autorizacion(aceptaciones.consentimiento)],
                ['Tratamiento de datos sensibles', autorizacion(aceptaciones.datos_sensibles)],
                ['Fecha de aceptación', fecha_aceptacion ? formatoFecha(fecha_aceptacion) : 'No registrada'],
            ]} />
        </TablaDetalle>
    )
}
